/**
 * 爱微圈导航 · 快捷方式渲染（图标 / 卡片 / 网格）
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, gateGuest, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackClick, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

// 前端快捷方式搜索过滤状态（模块级，供 renderCardView / renderShortcuts 共用）
export let shortcutFilterQuery = '';
export function highlightMatch(text, q) {
    if (!q) return esc(text || '');
    const t = text || '';
    const i = t.toLowerCase().indexOf(q);
    if (i === -1) return esc(t);
    return esc(t.slice(0, i)) + '<mark class="filter-hl">' + esc(t.slice(i, i + q.length)) + '</mark>' + esc(t.slice(i + q.length));
}


export function currentGrid() { return $('#shortcutsGrid'); }
// 系统「减弱动态效果」开启时跳过翻页滑动动画（也避免低性能设备上的动画卡顿）

export const _reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function waitTransition(el, timeout = 500) {
    return new Promise(resolve => {
        const done = () => resolve();
        el.addEventListener('transitionend', done, { once: true });
        setTimeout(done, timeout);
    });
}

export function renderSkeleton() {
    const cols = getGridCols();
    const grid = currentGrid();
    grid.className = 'shortcuts-grid';
    grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
    grid.innerHTML = Array.from({ length: cols * (parseInt(state.settings.grid_rows) || 3) }, () =>
        `<div class="skeleton-item"><div class="skeleton-icon"></div><div class="skeleton-line"></div></div>`).join('');
}

export function getGridCols() {
    const w = window.innerWidth;
    const cols = parseInt(state.settings.grid_cols) || 6;
    if (w <= 420) return 4;
    if (w <= 768) return Math.min(cols, 4);
    if (w <= 900) return Math.min(cols, 5);
    return cols;
}

export function faviconOf(url) {
    try {
        const u = new URL(/^https?:/i.test(url) ? url : 'https://' + url);
        return '/api/icon_proxy.php?url=' + encodeURIComponent(u.origin);
    } catch (e) { return ''; }
}
// 预连接目标域名，加速后续跳转

const _preconnectCache = new Set();

export function preconnectUrl(url) {
    try {
        const u = new URL(/^https?:/i.test(url) ? url : 'https://' + url);
        const origin = u.origin;
        if (_preconnectCache.has(origin)) return;
        _preconnectCache.add(origin);
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
    } catch (e) {}
}
// 图标加载结果缓存：已成功的 URL 直接复用（省去每次渲染都新建 img 等 onload），
// 失败的 URL 会话内不再重试（避免翻页时反复发起注定失败的请求）
/* 图标加载结果缓存：持久化到 localStorage。
 * 刷新后已知可加载的图标直接渲染 <img>（内存/磁盘缓存瞬时出图），
 * 不再经历「彩色字母块 → 白底 favicon」的整屏翻转——这是刷新闪屏的最大来源。 */

export function _readIconStore(key, cap) {
    try {
        const a = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(a) ? a.slice(-cap) : [];
    } catch (e) { return []; }
}

const _iconOkCache = new Set(_readIconStore('awq_icon_ok', 400));

const _iconFailCache = new Set(_readIconStore('awq_icon_fail', 200));

let _iconStoreTimer = null;

export function _saveIconStore() {
    clearTimeout(_iconStoreTimer);
    _iconStoreTimer = setTimeout(() => {
        try {
            // data: URL 体积大且无跨刷新价值，不持久化
            const ok = [..._iconOkCache].filter(u => !u.startsWith('data:')).slice(-400);
            const fail = [..._iconFailCache].filter(u => !u.startsWith('data:')).slice(-200);
            localStorage.setItem('awq_icon_ok', JSON.stringify(ok));
            localStorage.setItem('awq_icon_fail', JSON.stringify(fail));
        } catch (e) { /* 忽略配额错误 */ }
    }, 800);
}

export function renderIcon(container, item) {
    container.innerHTML = '';
    const rawIcon = item.icon_url || '';
    const iconUrl = (rawIcon && !rawIcon.includes('google.com/s2/favicons'))
        ? rawIcon
        : (safeUrl(item.url) ? faviconOf(item.url) : '');
    const text = (item.icon_text || (item.name || '?').charAt(0)).toUpperCase();
    container.textContent = text;
    container.style.background = item.icon_color || '#5B8DD4';
    container.style.color = '#fff';
    if (!iconUrl || _iconFailCache.has(iconUrl)) return;
    const applyImg = (img, fade) => {
        container.innerHTML = '';
        const isData = iconUrl.startsWith('data:');
        container.style.background = isData ? (item.icon_color || '#5B8DD4') : '#ffffff';
        img.classList.add('loaded');
        if (fade) img.classList.add('icon-fade'); // 仅延迟换图时淡入；已知好图标直出不播动画
        container.appendChild(img);
    };
    const onFail = () => {
        if (_iconFailCache.size > 200) _iconFailCache.clear();
        _iconFailCache.add(iconUrl);
        _saveIconStore();
        container.innerHTML = '';
        container.textContent = text;
        container.style.background = item.icon_color || '#5B8DD4';
        container.style.color = '#fff';
    };
    const img = document.createElement('img');
    img.src = iconUrl;
    img.alt = item.name || '';
    img.referrerPolicy = 'no-referrer';
    img.decoding = 'async';
    if (_iconOkCache.has(iconUrl)) {
        // 已成功加载过：浏览器内存缓存可瞬时出图，直接上屏，不再等 onload
        applyImg(img);
        return;
    }
    img.onload = () => {
        if (_iconOkCache.size > 500) _iconOkCache.clear();
        _iconOkCache.add(iconUrl);
        _saveIconStore();
        applyImg(img, true);
    };
    img.onerror = onFail;
    // 如果图片已在缓存中，立即显示
    if (img.complete && img.naturalWidth > 0) {
        if (_iconOkCache.size > 500) _iconOkCache.clear();
        _iconOkCache.add(iconUrl);
        _saveIconStore();
        applyImg(img);
    }
}
/* ---------- 渲染指纹：数据没变就跳过整树重建，消除刷新二次渲染闪屏 ---------- */

export function _scFp(list) {
    return JSON.stringify((list || []).map(s => [s.id, s.name, s.url, s.icon_url, s.icon_text, s.icon_color, s.position, s.is_folder, s.parent_id, s.category, s.description]));
}

export function gridRenderFp() {
    const st = state.settings;
    return 'g|' + _scFp(state.shortcuts)
        + '|' + (st.display_mode || '') + (st.grid_cols || '') + 'x' + (st.grid_rows || '')
        + '|h' + (st.hide_icon_name || '0') + '|a' + (st.animation_type || '')
        + '|o' + (state.organizeMode ? 1 : 0);
}

export function cardRenderFp() {
    const st = state.settings;
    return 'c|' + _scFp(state.shortcuts)
        + '|' + (st.card_cols || '') + '|d' + (st.show_description || '1') + '|m' + (st.marquee_desc || '0')
        + '|' + JSON.stringify(state.allCategories || null)
        + '|e' + (state.site.empty_shortcuts || '') + '|g' + (state.site.empty_shortcuts_group || '');
}


export async function renderCardViewAll() {
    const grid = currentGrid();
    // 60s TTL 内存缓存：反复进出卡片模式不再重拉全量数据；
    // 数据变更路径（loadShortcuts 等）会将 _cardAllAt 置 0 使缓存失效
    if (state._cardAllAt && Date.now() - state._cardAllAt < 60000 && state.allCategories) {
        renderCardView();
        return;
    }
    /* 并发去重：init 缓存首屏 / loadSettings / loadShortcuts 可能几乎同时触发本函数，
     * 无去重会并发打两个 all_shortcuts 请求并互相覆盖渲染（闪屏 + 浪费）。 */
    if (state._cardAllInflight) {
        try { await state._cardAllInflight; } catch (e) { /* 已由首发路径处理 */ }
        if (state._renderedFp !== cardRenderFp()) renderCardView();
        return;
    }
    /* 有本地数据（首屏 localStorage 缓存 / 上次渲染）时先秒开，不再显示「加载中…」，
     * 从根本上消除卡片模式刷新时「加载中… → 内容」的闪屏。 */
    const hasLocal = Array.isArray(state.shortcuts) && state.shortcuts.length > 0;
    if (hasLocal) {
        /* 与当前 DOM 一致（如 loadSettings 紧随 init 二次触发）则连本地渲染也跳过 */
        if (state._renderedFp !== cardRenderFp()) renderCardView();
    } else {
        grid.className = '';
        grid.style.cssText = '';
        grid.innerHTML = '<div style="padding:40px;text-align:center;color:#999">加载中…</div>';
    }
    state._cardAllInflight = (async () => {
        const [all, cats] = await Promise.all([
            apiGet(API.shortcuts + '?action=all_shortcuts'),
            apiGet(API.shortcuts + '?action=all_categories').catch(() => ({ categories: [], counts: {} }))
        ]);
        state.shortcuts = Array.isArray(all) ? all : [];
        state.allCategories = cats;
        state._cardAllAt = Date.now();
    })();
    try {
        await state._cardAllInflight;
        /* 服务器数据与已渲染内容一致（刷新但数据没变）→ 跳过重渲染，DOM 与已加载图标原样保留 */
        if (state._renderedFp !== cardRenderFp()) renderCardView();
    } catch(e) {
        if (state.shortcuts && state.shortcuts.length > 0) {
            if (state._renderedFp !== cardRenderFp()) renderCardView();
        } else {
            grid.innerHTML = '<div style="padding:40px;text-align:center;color:#999">加载失败，请刷新重试</div>';
        }
    } finally {
        state._cardAllInflight = null;
    }
}


export function renderCardView() {
    const wrap = currentGrid();
    wrap.className = 'shortcuts-card-view';
    wrap.innerHTML = '';

    if (!state.shortcuts || state.shortcuts.length === 0) {
        wrap.innerHTML = '<div style="padding:60px;text-align:center;color:#999;font-size:14px">' + (state.site.empty_shortcuts || '暂无快捷方式，点击右上角添加') + '</div>';
        return;
    }

    const filterQ = shortcutFilterQuery.toLowerCase().trim();
    const folders = state.shortcuts.filter(s => parseInt(s.is_folder) === 1);
    let items = state.shortcuts.filter(s => parseInt(s.is_folder) !== 1 && !s.parent_id);
    if (filterQ) {
        items = items.filter(s => (s.name || '').toLowerCase().includes(filterQ) || (s.url || '').toLowerCase().includes(filterQ));
    }
    const cols = parseInt(state.settings.card_cols) || 3;
    const showDesc = state.settings.show_description !== '0'; // 尊重"显示描述"设置项（此前硬编码 true 使其失效）
    const marquee = state.settings.marquee_desc === '1';

    // 按 category 分组
    const grouped = {};
    items.forEach(sc => {
        const cat = sc.category || '常用推荐';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(sc);
    });
    // 合并 API 返回的所有分组（包括空分组）
    const apiCats = state.allCategories?.categories || [];
    apiCats.forEach(cat => {
        if (!grouped[cat]) grouped[cat] = [];
    });

    // 渲染分组函数
    const renderSection = (title, icon, list, isFolder) => {
        if (list.length === 0) {
            // 空分组也显示
            const section = document.createElement('div');
            section.className = 'card-section';
            const secTitle = document.createElement('div');
            secTitle.className = 'card-section-title';
            secTitle.innerHTML = `<span class="sec-icon">${icon}</span><span>${esc(title)}</span><span class="sec-more">暂无</span>`;
            section.appendChild(secTitle);
            const empty = document.createElement('div');
            empty.style.cssText = 'padding:20px;text-align:center;color:rgba(255,255,255,0.3);font-size:13px';
            empty.textContent = state.site.empty_shortcuts_group || '暂无快捷方式，编辑快捷方式时可选择此分组';
            section.appendChild(empty);
            wrap.appendChild(section);
            return;
        }
        const section = document.createElement('div');
        section.className = 'card-section';

        const secTitle = document.createElement('div');
        secTitle.className = 'card-section-title';
        secTitle.innerHTML = `<span class="sec-icon">${icon}</span><span>${esc(title)}</span><span class="sec-more">共 ${list.length} 个</span>`;
        section.appendChild(secTitle);

        const grid = document.createElement('div');
        grid.className = `card-grid cols-${cols}`;

        list.forEach((sc, i) => {
            const card = document.createElement('div');
            card.className = 'site-card';
            card.dataset.id = sc.id;
            card.draggable = true;
            card.style.setProperty('--card-accent', sc.icon_color || '#5B8DD4');

            // 图标 - 懒加载优化
            const iconEl = document.createElement('div');
            iconEl.className = 'sc-icon';
            iconEl.style.background = sc.icon_color || '#5B8DD4';
            const iconText = (sc.icon_text || (sc.name || '?').charAt(0)).toUpperCase();
            iconEl.textContent = iconText;
            // 用 data 属性存储图标 URL，IntersectionObserver 懒加载
            const rawIcon = sc.icon_url || '';
            const iconUrl = (rawIcon && !rawIcon.includes('google.com/s2/favicons'))
                ? rawIcon
                : (safeUrl(sc.url) ? faviconOf(sc.url) : '');
            if (iconUrl && _iconOkCache.has(iconUrl)) {
                /* 已知可加载的图标（上次刷新成功过）：直接渲染，跳过懒加载，
                 * 浏览器内存/磁盘缓存瞬时出图，避免字母块→图片的翻转闪屏 */
                const img = document.createElement('img');
                img.src = iconUrl; img.alt = ''; img.referrerPolicy = 'no-referrer';
                img.classList.add('loaded');
                iconEl.textContent = '';
                iconEl.style.background = iconUrl.startsWith('data:') ? (sc.icon_color || '#5B8DD4') : 'rgba(255,255,255,0.92)';
                iconEl.appendChild(img);
            } else if (iconUrl && !_iconFailCache.has(iconUrl)) {
                iconEl.dataset.iconUrl = iconUrl;
            }
            card.appendChild(iconEl);

            // 信息
            const info = document.createElement('div');
            info.className = 'sc-info';
            const nameEl = document.createElement('div');
            nameEl.className = 'sc-name';
            nameEl.textContent = sc.name || '';
            info.appendChild(nameEl);

            // 描述：卡片模式始终显示
            const shouldShowDesc = showDesc && !isFolder;
            if (shouldShowDesc) {
                const descEl = document.createElement('div');
                descEl.className = 'sc-desc' + (marquee ? ' marquee' : '');
                const desc = sc.description || '';
                if (marquee && desc) {
                    descEl.innerHTML = `<span class="mq-text">${esc(desc)}</span>`;
                } else {
                    descEl.textContent = desc;
                }
                info.appendChild(descEl);
            }
            card.appendChild(info);

            // 编辑按钮（内置项不可编辑，与网格模式保持一致）
            if (parseInt(sc.is_builtin) !== 1) {
                const editBtn = document.createElement('button');
                editBtn.className = 'card-edit-btn';
                editBtn.textContent = '✎';
                editBtn.title = '编辑';
                editBtn.onclick = (e) => { e.stopPropagation(); showEditModal(sc); };
                card.appendChild(editBtn);
            }

            // 点击
            card.onclick = () => {
                if (state._justDragged) { state._justDragged = false; return; }
                if (isFolder) { showFolderModal(sc); return; }
                const url = safeUrl(sc.url);
                if (!url) { toast('无效的链接地址', 'error'); return; }
                preconnectUrl(url);
                if (state.settings.open_new_tab === '1') window.open(url, '_blank', 'noopener');
                else location.href = url;
            };
            card.onmouseenter = () => {
                if (isFolder) return;
                const url = safeUrl(sc.url);
                if (url) preconnectUrl(url);
            };
            card.oncontextmenu = (e) => { e.preventDefault(); showContextMenu(e, sc); };

            // 拖拽
            card.ondragstart = (e) => {
                if (gateGuest()) { e.preventDefault(); return; } // 排序属登录限定（2026-09-04 策略）
                state.dragged = sc;
                state.dragSource = 'grid';
                card.style.opacity = '0.5';
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', sc.id);
            };
            card.ondragend = () => {
                card.style.opacity = '';
                state._justDragged = true;
                setTimeout(() => { state._justDragged = false; }, 10);
            };
            card.ondragover = (e) => {
                e.preventDefault();
                if (state.dragged && state.dragged.id !== sc.id) {
                    if (isFolder) {
                        card.style.borderColor = 'var(--primary)';
                        card.style.boxShadow = '0 0 0 3px rgba(91, 141, 212, 0.4)';
                    } else {
                        /* 按落点上下半给卡片加 before/after 标记，提示插入位置；
                         * 解决「首张卡片无法直接移入顶部」的视觉盲区。 */
                        const rect = card.getBoundingClientRect();
                        const before = (e.clientY - rect.top) < rect.height / 2;
                        card.dataset.dropPos = before ? 'before' : 'after';
                        card.style.borderColor = 'var(--primary)';
                        card.style.boxShadow = before
                            ? 'inset 0 3px 0 0 var(--primary)'
                            : 'inset 0 -3px 0 0 var(--primary)';
                    }
                }
            };
            card.ondragleave = () => {
                card.style.borderColor = '';
                card.style.boxShadow = '';
                card.dataset.dropPos = '';
            };
            card.ondrop = (e) => {
                e.preventDefault();
                e.stopPropagation();
                card.style.borderColor = '';
                card.style.boxShadow = '';
                card.dataset.dropPos = '';
                if (!state.dragged || state.dragged.id === sc.id) return;
                // 拖到文件夹上 → 移入文件夹
                if (isFolder) {
                    apiPost(API.shortcuts + '?action=move_to_folder', { id: state.dragged.id, folder_id: sc.id }).then(() => {
                        state._justDragged = true;
                        state.shortcutCache = {};
                        loadShortcuts();
                        toast('已移入文件夹', 'success');
                    }).catch(err => toast(err.message, 'error'));
                    return;
                }
                /* 卡片模式跨页处理：登录用户 all_shortcuts 返回的 state.shortcuts 保留各元素的 page
                 * 字段（每页独立），但卡片视图本身把所有页渲染成一个滚动大列表。
                 * 若按 page 校验跨页拖动，用户看到的"大列表里拖动"会被拒绝。
                 * 改为：拖到目标位置时，把被拖元素的 page 改为目标元素的 page，
                 * 然后在 pageList（已按 page 顺序排好）里换位/插入；最终 reorder 时把改后的 page 一并提交。
                 * 这样无论登录/游客、跨页/同页，用户都能直接拖动。 */
                const fromIdx = state.shortcuts.findIndex(s => s.id === state.dragged.id);
                const toIdx = state.shortcuts.findIndex(s => s.id === sc.id);
                if (fromIdx === -1 || toIdx === -1) return;
                const moved = state.shortcuts.splice(fromIdx, 1)[0];
                /* 关键：吸附到目标元素的 page（登录用户的 page 字段保留下来） */
                moved.page = sc.page || 1;
                /* 根据落点 Y 在卡片的上半/下半决定「插到目标前」或「插到目标后」 */
                const rect = card.getBoundingClientRect();
                const before = (e.clientY - rect.top) < rect.height / 2;
                const insertAt = before ? toIdx : toIdx + 1;
                const finalPos = Math.max(0, Math.min(insertAt, state.shortcuts.length));
                state.shortcuts.splice(finalPos, 0, moved);
                /* 重新按 page 分组并重排 position（保持各页 position 连续） */
                const groups = {};
                state.shortcuts.forEach(s => {
                    const p = s.page || 1;
                    (groups[p] = groups[p] || []).push(s);
                });
                const items = [];
                Object.keys(groups).sort((a, b) => Number(a) - Number(b)).forEach(p => {
                    groups[p].forEach((s, idx) => items.push({ id: s.id, position: idx, page: Number(p) }));
                });
                apiPost(API.shortcuts + '?action=reorder', { items }).then(() => loadShortcuts()).catch(() => {});
            };

            grid.appendChild(card);
        });

        section.appendChild(grid);
        wrap.appendChild(section);
    };

    // 文件夹组
    if (folders.length > 0) {
        renderSection('文件夹', '📁', folders, true);
    }
    // 按分组顺序渲染
    const catOrder = ['常用推荐', '工具', '社交', '资讯', '购物', '视频', '游戏', '开发', '其他'];
    const cats = Object.keys(grouped).sort((a, b) => {
        const ia = catOrder.indexOf(a), ib = catOrder.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    const catIcons = {'常用推荐': '⭐', '工具': '🔧', '社交': '💬', '资讯': '📰', '购物': '🛒', '视频': '🎬', '游戏': '🎮', '开发': '💻', '其他': '📌'};
    cats.forEach(cat => {
        renderSection(cat, catIcons[cat] || '📌', grouped[cat], false);
    });

    // 懒加载图标：复用单例 observer 避免内存泄漏
    if ('IntersectionObserver' in window) {
        if (!window._cardIconObs) {
            window._cardIconObs = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const el = entry.target;
                        const url = el.dataset.iconUrl;
                        if (url) {
                            const img = document.createElement('img');
                            img.src = url; img.alt = ''; img.referrerPolicy = 'no-referrer';
                            img.onload = () => {
                                if (_iconOkCache.size > 500) _iconOkCache.clear();
                                _iconOkCache.add(url); _saveIconStore();
                                el.textContent = '';
                                el.style.background = 'rgba(255,255,255,0.92)';
                                img.classList.add('loaded', 'icon-fade');
                                el.appendChild(img);
                            };
                            img.onerror = () => {
                                if (_iconFailCache.size > 200) _iconFailCache.clear();
                                _iconFailCache.add(url); _saveIconStore();
                            };
                        }
                        delete el.dataset.iconUrl;
                        window._cardIconObs.unobserve(el);
                    }
                });
            }, { rootMargin: '100px' });
        }
        $$('.sc-icon[data-icon-url]', wrap).forEach(el => window._cardIconObs.observe(el));
    } else {
        $$('.sc-icon[data-icon-url]', wrap).forEach(el => {
            const url = el.dataset.iconUrl;
            if (url) {
                const img = document.createElement('img');
                img.src = url; img.alt = ''; img.referrerPolicy = 'no-referrer';
                img.onload = () => {
                    if (_iconOkCache.size > 500) _iconOkCache.clear();
                    _iconOkCache.add(url); _saveIconStore();
                    el.textContent = '';
                    el.style.background = 'rgba(255,255,255,0.92)';
                    img.classList.add('loaded', 'icon-fade');
                    el.appendChild(img);
                };
                img.onerror = () => {
                    if (_iconFailCache.size > 200) _iconFailCache.clear();
                    _iconFailCache.add(url); _saveIconStore();
                };
            }
            delete el.dataset.iconUrl;
        });
    }

    // 跑马灯速度：(文字宽度 + 40px间距) / 50px/s
    requestAnimationFrame(() => {
        $$('.sc-desc.marquee', wrap).forEach(descEl => {
            const text = descEl.querySelector('.mq-text');
            if (!text) return;
            const prevMaxWidth = text.style.maxWidth;
            text.style.maxWidth = 'none';
            const textWidth = text.scrollWidth;
            text.style.maxWidth = prevMaxWidth || '';
            const containerWidth = descEl.clientWidth;
            if (textWidth <= containerWidth) return;
            const totalWidth = textWidth + 40;
            const duration = totalWidth / 50;
            text.style.setProperty('--marquee-duration', duration + 's');
        });
    });
    state._renderedFp = cardRenderFp(); // 记录渲染指纹：下次数据没变可整体跳过重渲染
}

/* 拖拽实时挤开核心：把 dragEl 在 container 内移动到 targetEl 前/后，
 * 用 FLIP 技术让其它图标平滑让位。返回是否发生了位移。 */

export function flipMove(container, dragEl, targetEl, before) {
    if (!dragEl || dragEl === targetEl || !container) return false;
    // 已在目标相邻位置 → 无需移动，避免光标抖动导致的来回闪烁
    if (before && dragEl.nextElementSibling === targetEl) return false;
    if (!before && targetEl.nextElementSibling === dragEl) return false;

    const movable = Array.from(container.children).filter(el => el.dataset.id);
    if (movable.indexOf(dragEl) === -1 || movable.indexOf(targetEl) === -1) return false;

    // FLIP 首帧：记录所有可拖动项当前位置
    const first = new Map();
    movable.forEach(el => first.set(el, el.getBoundingClientRect()));

    // 移动 DOM 节点（insertBefore 自带「先摘除再插入」，无需手动算索引）
    if (before) {
        container.insertBefore(dragEl, targetEl);
    } else {
        container.insertBefore(dragEl, targetEl.nextElementSibling); // null 时自动追加到末尾
    }

    // FLIP 补间：让其余项从旧位置平滑滑到新位置（拖拽项自身不补间，直接落到新槽）
    movable.forEach(el => {
        if (el === dragEl) return;
        const f = first.get(el);
        if (!f) return;
        const l = el.getBoundingClientRect();
        const dx = f.left - l.left, dy = f.top - l.top;
        if (!dx && !dy) return;
        el.style.transition = 'none';
        el.style.transform = `translate(${dx}px, ${dy}px)`;
        void el.offsetWidth; // 强制回流，确保补间生效
        el.style.transition = 'transform 0.18s cubic-bezier(0.22, 1, 0.36, 1)';
        el.style.transform = '';
    });
    return true;
}

/* 网格排序持久化：把当前页 state.shortcuts 的顺序写回后端 */

export function persistGridReorder() {
    return apiPost(API.shortcuts + '?action=reorder', {
        items: state.shortcuts.map(s => ({ id: s.id, position: s.position, page: state.currentPage }))
    }).catch(() => {});
}


export function renderShortcuts() {
    
    const filterQ = shortcutFilterQuery.toLowerCase().trim();
// 卡片模式 - 加载所有快捷方式
    if (state.settings.display_mode === 'card') {
        renderCardViewAll();
        return;
    }
    /* grid 模式每页只应显示 getPageCapacity() 条。
     * 防御：若 state.shortcuts 混入了超量数据（例如 card 模式缓存下来的全量），
     * 先按容量裁剪再渲染，否则会一次性渲染出好几页的行数，随后被服务端分页数据
     * 替换而大幅跳动（用户反馈的「刷新后差十几行」）。 */
    const _cap = getPageCapacity();
    if (_cap > 0 && state.shortcuts.length > _cap) {
        state.shortcuts = state.shortcuts.slice(0, _cap);
    }
    const grid = currentGrid();
    const cols = getGridCols();
    grid.className = 'shortcuts-grid';
    grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
    grid.innerHTML = '';
    const fragment = document.createDocumentFragment();
    const anim = 'anim-' + (state.settings.animation_type || 'default');

    state.shortcuts.forEach((sc, i) => {
        const item = document.createElement('div');
        const isFolder = parseInt(sc.is_folder) === 1;
        // 只在「真正首次访问」（无本地缓存）时播放入场动画；刷新时有缓存直接静态呈现，
        // 否则每次刷新整屏图标都波浪式淡入一次，视觉上就是「闪」。
        const showAnim = shouldAnimate() && !state._rendered && !state._skipIntroAnim;
        item.className = `shortcut-item ${showAnim ? anim : ''} ${isFolder ? 'is-folder' : ''}`;
        if (showAnim) item.style.animationDelay = (i * 0.035) + 's';
        item.draggable = !state.organizeMode;
        item.dataset.id = sc.id;
        item.dataset.index = i;

        const icon = document.createElement('div');
        icon.className = 'shortcut-icon';
        renderIcon(icon, sc);
        if (isFolder) {
            const badge = document.createElement('div');
            badge.className = 'folder-badge';
            badge.innerHTML = '<svg viewBox="0 0 24 24"><path d="M10 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-8l-2-2z" fill="rgba(255,255,255,0.9)"/></svg>';
            icon.appendChild(badge);
        }

        const name = document.createElement('div');
        name.className = 'shortcut-name';
        name.innerHTML = filterQ ? highlightMatch(sc.name, filterQ) : esc(sc.name || '');
        name.title = sc.name || '';

const edit = document.createElement('button');
        edit.className = 'shortcut-edit-btn';
        edit.textContent = '✎';
        edit.onclick = (e) => { e.stopPropagation(); showEditModal(sc); };

        // 批量整理模式：注入复选框，点击切换选中而非打开链接
        if (state.organizeMode) {
            const cb = document.createElement('div');
            cb.className = 'sc-checkbox' + (state.selectedIds.has(sc.id) ? ' checked' : '');
            cb.innerHTML = '<svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>';
            item.classList.toggle('selected', state.selectedIds.has(sc.id));
            item.append(cb);
        }

        item.append(icon);
        if (state.settings.hide_icon_name !== '1') item.append(name);
        if (parseInt(sc.is_builtin) !== 1) item.append(edit);

        item.onclick = () => {
            if (state._justDragged) { state._justDragged = false; return; }
            if (state.organizeMode) {
                if (state.selectedIds.has(sc.id)) state.selectedIds.delete(sc.id);
                else state.selectedIds.add(sc.id);
                updateBatchToolbar();
                renderShortcuts();
                return;
            }
            if (isFolder) { showFolderModal(sc); return; }
            if (parseInt(sc.is_builtin) === 1 && BUILTIN_ACTIONS[sc.name]) { BUILTIN_ACTIONS[sc.name](); return; }
            const url = safeUrl(sc.url);
            if (!url) { toast('无效的链接地址', 'error'); return; }
            // 点击时再预连接一次（确保触发）
            preconnectUrl(url);
            trackClick(url, sc.name || '');
            if (state.settings.open_new_tab === '1') window.open(url, '_blank', 'noopener');
            else location.href = url;
        };
        // hover 时预连接目标域名，加速打开
        item.onmouseenter = () => {
            if (isFolder || parseInt(sc.is_builtin) === 1) return;
            const url = safeUrl(sc.url);
            if (url) preconnectUrl(url);
        };
        item.oncontextmenu = (e) => { e.preventDefault(); showContextMenu(e, sc); };

        /* 拖拽排序：实时挤开 + 中心合并 */
        item.ondragstart = (e) => {
            if (gateGuest()) { e.preventDefault(); return; } // 排序属登录限定（2026-09-04 策略）
            state.dragged = sc;
            state.dragSource = 'grid';
            state._dragEl = item;
            state._dragDone = false;
            state._dragStartOrder = state.shortcuts.map(s => s.id);
            item.classList.add('dragging');
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', sc.id);
        };
        item.ondragend = () => {
            item.classList.remove('dragging');
            $$('.drop-target', grid).forEach(x => x.classList.remove('drop-target'));
            $$('.merge-target', grid).forEach(x => x.classList.remove('merge-target'));
            grid.classList.remove('drag-over');
            // 拖到空白处松开（无 drop 处理）时，若实时挤开已改变顺序则补一次持久化
            if (!state._dragDone && state.dragSource === 'grid' && state.dragged) {
                const order = state.shortcuts.map(s => s.id);
                const changed = order.length !== state._dragStartOrder.length ||
                    order.some((id, idx) => id !== state._dragStartOrder[idx]);
                if (changed) persistGridReorder();
            }
            state.dragged = null;
            state._dragEl = null;
            state.dragSource = null;
            setTimeout(() => { state._justDragged = false; }, 10);
        };
        item.ondragover = (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (!state.dragged || state.dragged.id === sc.id) return;
            const rect = item.getBoundingClientRect();
            const cx = e.clientX - rect.left;
            const cy = e.clientY - rect.top;
            // 拖到普通图标中心 → 合并成文件夹（优先于排序挤开）
            const inCenter = !isFolder &&
                cx > rect.width * 0.35 && cx < rect.width * 0.65 &&
                cy > rect.height * 0.2 && cy < rect.height * 0.7;
            if (inCenter) {
                item.classList.remove('drop-target');
                item.classList.add('merge-target');
                return;
            }
            item.classList.remove('merge-target');
            // 实时挤开：按光标在目标左/右半决定插到其前/后（网格内排序）
            if (state.dragSource === 'grid' && !isFolder) {
                const before = cx < rect.width * 0.5;
                flipMove(grid, state._dragEl, item, before);
                item.classList.remove('drop-target');
                // 同步 state.shortcuts 顺序，供 drop/dragend 持久化
                const ids = [...grid.querySelectorAll('.shortcut-item[data-id]')].map(n => parseInt(n.dataset.id, 10));
                const byId = new Map(state.shortcuts.map(s => [s.id, s]));
                state.shortcuts = ids.map(id => byId.get(id)).filter(Boolean);
                state.shortcuts.forEach((s, idx) => s.position = idx);
            } else {
                item.classList.add('drop-target');
            }
        };
        item.ondragleave = (e) => {
            if (!item.contains(e.relatedTarget)) {
                item.classList.remove('drop-target', 'merge-target');
            }
        };
        item.ondrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const isMerge = item.classList.contains('merge-target');
            item.classList.remove('drop-target', 'merge-target');
            grid.classList.remove('drag-over');
            if (!state.dragged || state.dragged.id === sc.id) return;
            state._dragDone = true; // 以下任一分支都会完成本次拖放，避免 dragend 重复持久化
            // 关键：ondragend 会在 drop 之后立即触发并把 state.dragged 清空，
            // 但下面 .then() 是异步的，到那时再读 state.dragged.id 就会报 "null is not an object"。
            // 在 drop 同步阶段把要用到的值先抓到本地，闭包里就安全了。
            const draggedId = state.dragged.id;
            const srcKind = state.dragSource; // 'grid' 或 'folder'

            // 从文件夹拖出来，放到某个快捷方式上 → 移出文件夹并排到该位置
            if (srcKind === 'folder') {
                const insertIdx = i;
                apiPost(API.shortcuts + '?action=remove_from_folder', { id: draggedId, page: state.currentPage }).then(() => {
                    return loadShortcutsRaw().then(list => {
                        const newItem = list.find(s => s.id === draggedId);
                        if (newItem) {
                            const rest = list.filter(s => s.id !== draggedId);
                            rest.splice(insertIdx, 0, newItem);
                            rest.forEach((s, idx) => s.position = idx);
                            return apiPost(API.shortcuts + '?action=reorder', {
                                items: rest.map(s => ({ id: s.id, position: s.position, page: state.currentPage }))
                            });
                        }
                    });
                }).then(() => {
                    state._justDragged = true;
                    loadShortcuts();
                    toast('已移出文件夹', 'success');
                }).catch(err => toast(err.message, 'error'));
                return;
            }

            // 放到文件夹上 → 移入文件夹
            if (isFolder) {
                apiPost(API.shortcuts + '?action=move_to_folder', { id: draggedId, folder_id: sc.id }).then(() => {
                    state._justDragged = true;
                    state.shortcuts = state.shortcuts.filter(s => s.id !== draggedId);
                    renderShortcuts();
                    toast('已移入文件夹', 'success');
                }).catch(err => toast(err.message, 'error'));
                return;
            }

            // 拖到中心区域 → 合并成文件夹
            if (isMerge) {
                apiPost(API.shortcuts + '?action=create_folder', {
                    name: '文件夹',
                    page: state.currentPage,
                    icon_color: '#5B8DD4',
                    icon_text: '📁'
                }).then(r => {
                    return apiPost(API.shortcuts + '?action=move_to_folder', { id: draggedId, folder_id: r.id }).then(() => {
                        return apiPost(API.shortcuts + '?action=move_to_folder', { id: sc.id, folder_id: r.id });
                    });
                }).then(() => {
                    state._justDragged = true;
                    state.shortcutCache = {};
                    loadShortcuts();
                    toast('已合并为文件夹', 'success');
                }).catch(err => toast(err.message, 'error'));
                return;
            }

            // 普通排序：实时挤开已就位，此处仅持久化
            state._justDragged = true;
            persistGridReorder();
        };
        fragment.appendChild(item);
    });
    grid.appendChild(fragment);
    state._rendered = true;
    state._renderedFp = gridRenderFp(); // 记录渲染指纹：下次数据没变可整体跳过重渲染
    grid.ondragover = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (state.dragged && state.dragSource === 'folder') {
            grid.classList.add('drag-over');
        }
    };
    grid.ondragleave = (e) => {
        if (e.target === grid && !grid.contains(e.relatedTarget)) {
            grid.classList.remove('drag-over');
        }
    };
    grid.ondrop = (e) => {
        e.preventDefault();
        grid.classList.remove('drag-over');
        if (!state.dragged || state.dragSource !== 'folder') return;
        // 从文件夹拖到网格空白处 → 追加到末尾
        apiPost(API.shortcuts + '?action=remove_from_folder', { id: state.dragged.id, page: state.currentPage }).then(() => {
            state._justDragged = true;
            loadShortcuts();
            toast('已移出文件夹', 'success');
        }).catch(err => toast(err.message, 'error'));
    };

    const rows = parseInt(state.settings.grid_rows) || 3;
    if (state.shortcuts.length < cols * rows) {
        const add = document.createElement('div');
        add.className = `shortcut-item shortcut-add ${shouldAnimate() ? anim : ''}`;
        add.innerHTML = '<div class="shortcut-icon"><svg viewBox="0 0 24 24" style="width:46%;height:46%;fill:currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z"/></svg></div>';
        if (state.settings.hide_icon_name !== '1') {
            const n = document.createElement('div');
            n.className = 'shortcut-name'; n.textContent = '添加';
            add.appendChild(n);
        }
        add.onclick = () => openSidebar('add');
        grid.appendChild(add);
    }

    // 网格底部空白占位：补齐到 cols*rows，让网格行数固定为 grid_rows，
    // 分页指示器位置随之固定，不会因为图标少而向上飘。
    // 占位 cell 用 shortcut-empty 标记，CSS 设为 visibility:hidden + pointer-events:none，
    // 不响应点击/拖拽/hover，也不影响 drag-drop 的 closest('.shortcut-item') 跳过。
    const cap = cols * rows;
    const filled = grid.children.length;
    for (let i = filled; i < cap; i++) {
        const ph = document.createElement('div');
        ph.className = 'shortcut-item shortcut-empty';
        ph.setAttribute('aria-hidden', 'true');
        grid.appendChild(ph);
    }

    // 空白处拖动换页（非拖拽快捷方式时）
    if (!grid._swipeBound) {
        grid._swipeBound = true;
        let touchStartX = 0, touchStartY = 0, touchActive = false;
        grid.addEventListener('touchstart', (e) => {
            if (e.target.closest('.shortcut-item')) return;
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
            touchActive = true;
        }, { passive: true });
        grid.addEventListener('touchend', (e) => {
            if (!touchActive) return;
            touchActive = false;
            const dx = e.changedTouches[0].clientX - touchStartX;
            const dy = e.changedTouches[0].clientY - touchStartY;
            if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                const pages = state.pages.length || 1;
                const curIdx = state.pages.indexOf(state.currentPage);
                if (dx < 0 && curIdx < pages - 1) switchPage(state.pages[curIdx + 1]);
                else if (dx > 0 && curIdx > 0) switchPage(state.pages[curIdx - 1]);
            }
        }, { passive: true });
        // 鼠标拖动换页
        let mouseStartX = 0, mouseActive = false;
        grid.addEventListener('mousedown', (e) => {
            if (e.target.closest('.shortcut-item') || e.button !== 0) return;
            mouseStartX = e.clientX;
            mouseActive = true;
        });
        grid.addEventListener('mouseup', (e) => {
            if (!mouseActive) return;
            mouseActive = false;
            const dx = e.clientX - mouseStartX;
            if (Math.abs(dx) > 80) {
                const curIdx = state.pages.indexOf(state.currentPage);
                if (dx < 0 && curIdx < state.pages.length - 1) switchPage(state.pages[curIdx + 1]);
                else if (dx > 0 && curIdx > 0) switchPage(state.pages[curIdx - 1]);
            }
        });
        grid.addEventListener('mouseleave', () => { mouseActive = false; });
    }

    renderPages();
    cacheState();
}

